import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { ArrowLeft, Plus, Trash2, ImageIcon, Calendar, MapPin, Users, Check, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { updateEvent, getEventForEdit } from "@/lib/events.functions";
import { supabase } from "@/integrations/supabase/client";
import { categories } from "@/lib/mock-data";

export const Route = createFileRoute("/edit-event/$eventId")({
  component: EditEvent,
  head: () => ({
    meta: [
      { title: "Editar Evento — ZYNTRA" },
      { name: "description", content: "Editá los datos de tu evento." },
    ],
  }),
});

type TicketTypeForm = {
  id: string;
  name: string;
  price: string;
  total: string;
  description: string;
};

function EditEvent() {
  const { eventId } = Route.useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [loadingEvent, setLoadingEvent] = useState(true);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("20:00");
  const [location, setLocation] = useState("");
  const [city, setCity] = useState("");
  const [category, setCategory] = useState("Música");
  const [imageUrl, setImageUrl] = useState("");
  const [maxPerUser, setMaxPerUser] = useState("4");
  const [isPublished, setIsPublished] = useState(true);
  const [ticketTypes, setTicketTypes] = useState<TicketTypeForm[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (authLoading || !user) return;
    loadEvent();
  }, [user, authLoading]);

  async function loadEvent() {
    setLoadingEvent(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      const result = await getEventForEdit({
        data: { eventId },
        headers: token ? { authorization: `Bearer ${token}` } : undefined,
      } as any);
      const evt = result.event;
      setTitle(evt.title);
      setDescription(evt.description || "");
      setDate(evt.date);
      setTime(evt.time?.slice(0, 5) || "20:00");
      setLocation(evt.location);
      setCity(evt.city);
      setCategory(evt.category);
      setImageUrl(evt.image_url || "");
      setMaxPerUser(String(evt.max_per_user));
      setIsPublished(evt.is_published);
      setTicketTypes(
        (evt.ticket_types || []).map((tt: any) => ({
          id: tt.id,
          name: tt.name,
          price: String(tt.price),
          total: String(tt.total),
          description: tt.description || "",
        }))
      );
    } catch (err: any) {
      setError(err.message || "Error al cargar evento");
    } finally {
      setLoadingEvent(false);
    }
  }

  if (!authLoading && !user) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground">Iniciá sesión</h2>
          <Link to="/login" className="mt-4 inline-block rounded-xl gradient-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">Ingresar</Link>
        </div>
      </div>
    );
  }

  if (loadingEvent) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (saved) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-success/20">
            <Check className="h-8 w-8 text-success" />
          </div>
          <h2 className="text-2xl font-bold text-foreground">¡Evento actualizado!</h2>
          <div className="mt-6 flex justify-center gap-3">
            <Link to="/events/$eventId" params={{ eventId }} className="rounded-xl gradient-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">Ver evento</Link>
            <Link to="/dashboard" className="rounded-xl border border-border bg-secondary px-5 py-2.5 text-sm font-semibold text-secondary-foreground">Dashboard</Link>
          </div>
        </div>
      </div>
    );
  }

  function addTicketType() {
    setTicketTypes((prev) => [...prev, { id: crypto.randomUUID(), name: "", price: "", total: "", description: "" }]);
  }

  function removeTicketType(id: string) {
    if (ticketTypes.length <= 1) return;
    setTicketTypes((prev) => prev.filter((t) => t.id !== id));
  }

  function updateTicketType(id: string, field: keyof TicketTypeForm, value: string) {
    setTicketTypes((prev) => prev.map((t) => (t.id === id ? { ...t, [field]: value } : t)));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (!title.trim()) { setError("El título es obligatorio"); return; }
    if (!date) { setError("La fecha es obligatoria"); return; }
    if (!location.trim()) { setError("La ubicación es obligatoria"); return; }
    if (!city.trim()) { setError("La ciudad es obligatoria"); return; }

    const parsedTickets = ticketTypes.map((tt) => ({
      name: tt.name.trim(),
      price: parseFloat(tt.price) || 0,
      total: parseInt(tt.total) || 0,
      description: tt.description.trim(),
    }));

    for (const tt of parsedTickets) {
      if (!tt.name) { setError("Todos los tipos de ticket necesitan nombre"); return; }
      if (tt.total <= 0) { setError(`"${tt.name}" necesita una cantidad mayor a 0`); return; }
    }

    setSubmitting(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      await updateEvent({
        data: {
          eventId,
          title: title.trim(),
          description: description.trim() || undefined,
          date,
          time,
          location: location.trim(),
          city: city.trim(),
          category,
          imageUrl: imageUrl.trim() || undefined,
          maxPerUser: parseInt(maxPerUser) || 4,
          isPublished,
          ticketTypes: parsedTickets,
        },
        headers: token ? { authorization: `Bearer ${token}` } : undefined,
      } as any);
      setSaved(true);
    } catch (err: any) {
      setError(err.message || "Error al guardar");
    } finally {
      setSubmitting(false);
    }
  }

  const eventCategories = categories.filter((c) => c !== "Todos");

  return (
    <div className="mx-auto max-w-3xl px-4 pb-20 pt-6 sm:px-6">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
        <ArrowLeft className="h-4 w-4" />
        Dashboard
      </Link>

      <h1 className="text-2xl font-black text-foreground sm:text-3xl">Editar Evento</h1>
      <p className="mt-1 text-sm text-muted-foreground mb-8">Modificá los datos de tu evento.</p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <section className="rounded-2xl border border-border bg-card p-5 space-y-4">
          <h2 className="text-lg font-bold text-card-foreground">Detalles del evento</h2>

          <div>
            <label className="text-xs font-medium text-muted-foreground">Título *</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} className="mt-1 w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">Descripción</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={2000} className="mt-1 w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1"><Calendar className="h-3 w-3" /> Fecha *</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Hora</label>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1 w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> Ubicación *</label>
              <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} maxLength={200} className="mt-1 w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Ciudad *</label>
              <input type="text" value={city} onChange={(e) => setCity(e.target.value)} maxLength={100} className="mt-1 w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Categoría</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1 w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
                {eventCategories.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1"><Users className="h-3 w-3" /> Máx. por persona</label>
              <input type="number" value={maxPerUser} onChange={(e) => setMaxPerUser(e.target.value)} min={1} max={20} className="mt-1 w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1"><ImageIcon className="h-3 w-3" /> URL de imagen</label>
            <input type="url" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://..." className="mt-1 w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>

          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setIsPublished(!isPublished)} className={`relative h-6 w-11 rounded-full transition-colors ${isPublished ? "bg-primary" : "bg-muted"}`}>
              <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform ${isPublished ? "translate-x-5" : ""}`} />
            </button>
            <span className="text-sm text-foreground">{isPublished ? "Publicado" : "Borrador"}</span>
          </div>
        </section>

        <section className="rounded-2xl border border-border bg-card p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-card-foreground">Tipos de entrada</h2>
            <button type="button" onClick={addTicketType} className="flex items-center gap-1 rounded-lg bg-accent px-3 py-1.5 text-xs font-medium text-accent-foreground transition-colors hover:bg-primary/10">
              <Plus className="h-3 w-3" /> Agregar
            </button>
          </div>

          <div className="space-y-3">
            {ticketTypes.map((tt, idx) => (
              <div key={tt.id} className="rounded-xl border border-border bg-secondary/50 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-muted-foreground uppercase">Tipo {idx + 1}</span>
                  {ticketTypes.length > 1 && (
                    <button type="button" onClick={() => removeTicketType(tt.id)} className="rounded-lg p-1 text-destructive/60 hover:text-destructive hover:bg-destructive/10 transition-colors">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-3 sm:col-span-1">
                    <input type="text" value={tt.name} onChange={(e) => updateTicketType(tt.id, "name", e.target.value)} placeholder="Nombre *" maxLength={100} className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                  </div>
                  <div>
                    <input type="number" value={tt.price} onChange={(e) => updateTicketType(tt.id, "price", e.target.value)} placeholder="Precio ($)" min={0} className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                    {(parseFloat(tt.price) || 0) === 0 && tt.price !== "" && (
                      <p className="mt-1 text-[10px] font-medium text-primary">Gratis · sin pago · no reventa</p>
                    )}
                  </div>
                  <div>
                    <input type="number" value={tt.total} onChange={(e) => updateTicketType(tt.id, "total", e.target.value)} placeholder="Cantidad *" min={1} className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                  </div>
                </div>
                <input type="text" value={tt.description} onChange={(e) => updateTicketType(tt.id, "description", e.target.value)} placeholder="Descripción (opcional)" maxLength={500} className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
              </div>
            ))}
          </div>
        </section>

        {error && (
          <div className="rounded-xl bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">{error}</div>
        )}

        <button type="submit" disabled={submitting} className="flex w-full items-center justify-center gap-2 rounded-xl gradient-primary py-3.5 text-sm font-bold text-primary-foreground transition-all hover:opacity-90 glow-primary disabled:opacity-50">
          {submitting ? "Guardando..." : "Guardar cambios"}
        </button>
      </form>
    </div>
  );
}
